import { useEffect, useState } from "react";
import Quiz from "./Quiz";
import AuthorProfilePage from "./AuthorProfilePage";
import QuizRating from "../components/QuizRating";
import Pagination, { QUIZ_GRID_PAGE_SIZE } from "../components/Pagination";
import shuffleQuiz from "../utils/shuffleQuiz";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const API_URL = process.env.REACT_APP_QUIZ_API_URL || apiUrl("quizzes.php");
const SCHOOL_CLASSROOMS_API_URL = process.env.REACT_APP_CATEGORIES_API_URL
    || apiUrl("categories.php");
const START_ATTEMPT_API_URL = process.env.REACT_APP_START_ATTEMPT_API_URL
    || apiUrl("start_attempt.php");

async function fetchQuizzes(signal) {
    const response = await fetch(API_URL, { signal });
    if (!response.ok) {
        throw new Error(`Quiz API returned HTTP ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.toLowerCase().includes("application/json")) {
        throw new Error(
            `Expected JSON from ${response.url}, but received ${contentType || "an unknown content type"}. ` +
            "Check that the PHP endpoint is deployed and returns JSON."
        );
    }

    let data;
    try {
        data = await response.json();
    } catch (error) {
        throw new Error(`Quiz API returned invalid JSON: ${error.message}`);
    }
    if (!Array.isArray(data)) {
        throw new Error("Quiz API response must be an array");
    }

    return data;
}

export default function QuizesPage({ language, text, user }) {
    const [quizzes, setQuizzes] = useState([]);
    const [schoolClassrooms, setSchoolClassrooms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [reloadRequest, setReloadRequest] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSchoolTrack, setSelectedSchoolTrack] = useState("");
    const [quizPage, setQuizPage] = useState(1);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [startingQuizId, setStartingQuizId] = useState(null);
    const [startError, setStartError] = useState("");
    const [selectedAuthorId, setSelectedAuthorId] = useState(null);

    const startQuiz = async (quiz) => {
        setStartingQuizId(quiz.id);
        setStartError("");
        try {
            const response = await fetch(START_ATTEMPT_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ quizId: quiz.id })
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `Quiz start API returned HTTP ${response.status}`);
            }
            if (!Number.isSafeInteger(result.attemptId) || result.attemptId < 1) {
                throw new Error("Quiz start API did not return a valid attempt ID");
            }
            return result.attemptId;
        } catch (error) {
            console.error("Failed to start quiz:", error);
            setStartError(text.startQuizError);
            return null;
        } finally {
            setStartingQuizId(null);
        }
    };

    const handleStartQuiz = async (quiz) => {
        const attemptId = await startQuiz(quiz);
        if (attemptId) {
            setSelectedQuiz({ quiz: shuffleQuiz(quiz), attemptId });
        }
    };

    const handleRestartQuiz = async (quiz) => {
        const attemptId = await startQuiz(quiz);
        if (attemptId) {
            setSelectedQuiz({ quiz: shuffleQuiz(quiz), attemptId });
            return attemptId;
        }
        return null;
    };

    const handleRatingUpdated = (quizId, result) => {
        setQuizzes((currentQuizzes) => currentQuizzes.map((quiz) => (
            quiz.id === quizId
                ? {
                    ...quiz,
                    userRating: result.userRating,
                    averageRating: result.averageRating,
                    ratingCount: result.ratingCount
                }
                : quiz
        )));
    };

    useEffect(() => {
        const controller = new AbortController();
        Promise.all([
            fetchQuizzes(controller.signal),
            fetch(SCHOOL_CLASSROOMS_API_URL, { signal: controller.signal })
                .then(async (response) => {
                    const result = await response.json();
                    if (!response.ok) {
                        throw new Error(result.error || `Classroom API returned HTTP ${response.status}`);
                    }
                    if (!Array.isArray(result.schoolClassrooms)) {
                        throw new Error("Classroom API returned invalid classrooms");
                    }
                    return result.schoolClassrooms;
                })
        ])
            .then(([quizList, classrooms]) => {
                setQuizzes(quizList);
                setSchoolClassrooms(classrooms);
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    console.error("Failed to load quizzes:", error);
                    setLoadError(error.message);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [reloadRequest]);

    const handleRetry = () => {
        setIsLoading(true);
        setLoadError("");
        setReloadRequest((request) => request + 1);
    };

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredQuizzes = quizzes.filter((quiz) =>
        (!selectedSchoolTrack || quiz.schoolTrack === selectedSchoolTrack)
        &&
        `${quiz.title} ${quiz.description}`
            .concat(` ${quiz.authorName || ""} ${quiz.difficulty || ""}`)
            .toLowerCase()
            .includes(normalizedSearch)
    );
    const quizPageCount = Math.ceil(filteredQuizzes.length / QUIZ_GRID_PAGE_SIZE);
    const currentQuizPage = Math.min(quizPage, Math.max(1, quizPageCount));
    const paginatedQuizzes = filteredQuizzes.slice(
        (currentQuizPage - 1) * QUIZ_GRID_PAGE_SIZE,
        currentQuizPage * QUIZ_GRID_PAGE_SIZE
    );

    if (selectedQuiz) {
        return (
            <Quiz
                quiz={selectedQuiz.quiz}
                attemptId={selectedQuiz.attemptId}
                language={language}
                text={text}
                onBack={() => setSelectedQuiz(null)}
                onRestart={handleRestartQuiz}
                onRatingUpdated={handleRatingUpdated}
            />
        );
    }

    if (selectedAuthorId !== null) {
        return (
            <AuthorProfilePage
                authorId={selectedAuthorId}
                quizzes={quizzes}
                user={user}
                text={text}
                onBack={() => setSelectedAuthorId(null)}
                onStartQuiz={handleStartQuiz}
                onRatingUpdated={handleRatingUpdated}
            />
        );
    }

    return (
        <main className="quiz-catalog">
            <header className="catalog-header">
                <span className="quiz-label">{text.learnSomethingNew}</span>
                <h1>{text.chooseQuiz}</h1>
                <p>{text.catalogDescription}</p>
            </header>

            {startError && <p className="quiz-empty-state" role="alert">{startError}</p>}

            {isLoading ? (
                <p className="quiz-empty-state" role="status">
                    {text.loadingQuizzes}
                </p>
            ) : loadError ? (
                <div className="quiz-empty-state" role="alert">
                    <p>{text.loadQuizzesError}</p>
                    <p className="quiz-api-error">{loadError}</p>
                    <button
                        className="quiz-btn"
                        type="button"
                        onClick={handleRetry}
                    >
                        {text.retry}
                    </button>
                </div>
            ) : (
                <>
                    <label className="quiz-search-label" htmlFor="quiz-search">
                        {text.searchLabel}
                    </label>
                    <input
                        id="quiz-search"
                        className="quiz-search"
                        type="search"
                        placeholder={text.searchPlaceholder}
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setQuizPage(1);
                        }}
                        dir={language === "ar" ? "rtl" : "ltr"}
                    />

                    <div className="school-track-filter">
                        <label className="quiz-search-label" htmlFor="school-track-filter">
                            {text.schoolClassroom}
                        </label>
                        <select
                            id="school-track-filter"
                            className="quiz-search school-track-select"
                            value={selectedSchoolTrack}
                            onChange={(event) => {
                                setSelectedSchoolTrack(event.target.value);
                                setQuizPage(1);
                            }}
                            dir={language === "ar" ? "rtl" : "ltr"}
                        >
                            <option value="">{text.allSchoolTracks}</option>
                            {schoolClassrooms.map((classroom) => (
                                <option key={classroom.id} value={classroom.name}>
                                    {classroom.name === "General"
                                        ? text.generalClassroom
                                        : classroom.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {filteredQuizzes.length > 0 ? (
                        <div className="quiz-catalog-grid">
                            {paginatedQuizzes.map((quiz) => (
                                <article className="quiz-tile" key={quiz.id}>
                                    <div className="quiz-tile-meta">
                                        <span className={`quiz-difficulty ${quiz.difficulty}`}>
                                            {quiz.difficulty === "hard"
                                                ? text.hardDifficulty
                                                : text.easyDifficulty}
                                        </span>
                                        {quiz.isNew && (
                                            <span className="quiz-new-badge">{text.newQuiz}</span>
                                        )}
                                    </div>
                                    <div className="quiz-tile-classification">
                                        <span><strong>{text.schoolClassroom}:</strong> {quiz.schoolTrack}</span>
                                        <span><strong>{text.subjectLabel}:</strong> {quiz.category}</span>
                                    </div>
                                    <h2>{quiz.title}</h2>
                                    <p>{quiz.description}</p>
                                    {quiz.authorId ? (
                                        <button
                                            className="quiz-author-link"
                                            type="button"
                                            onClick={() => setSelectedAuthorId(quiz.authorId)}
                                        >
                                            {text.createdBy} {quiz.authorName}
                                        </button>
                                    ) : (
                                        <span className="quiz-author-name">{text.authorUnknown}</span>
                                    )}
                                    <QuizRating
                                        quizId={quiz.id}
                                        averageRating={quiz.averageRating}
                                        ratingCount={quiz.ratingCount}
                                        userRating={quiz.userRating}
                                        disabled={startingQuizId !== null}
                                        text={text}
                                        onRated={(result) => handleRatingUpdated(quiz.id, result)}
                                    />
                                    <span className="quiz-tile-count">
                                        {quiz.questions.length} {text.questions}
                                        {" · "}
                                        {quiz.participantCount} {text.quizParticipants}
                                    </span>
                                    <button
                                        className="quiz-btn"
                                        type="button"
                                        disabled={startingQuizId !== null}
                                        onClick={() => handleStartQuiz(quiz)}
                                    >
                                        {startingQuizId === quiz.id
                                            ? text.startingQuiz
                                            : text.startQuiz}
                                    </button>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <p className="quiz-empty-state" role="status">
                            {text.noQuizzes}
                        </p>
                    )}
                    <Pagination
                        page={currentQuizPage}
                        pageSize={QUIZ_GRID_PAGE_SIZE}
                        totalItems={filteredQuizzes.length}
                        text={text}
                        onPageChange={setQuizPage}
                    />
                </>
            )}
        </main>
    );
}